/* eslint-disable max-len */
import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { startCase } from "lodash";

import { log } from "../../utils/loggger";

import {
  SEND_CUSTOMER_CONSENT_TO_CPF,
  SEND_KFS_MAIL,
  termsAndConditionsAccepted,
  termsAndConditionsLoading
} from "../actions/termsAndConditions";
import {
  getAccountType,
  getApplicantEmail,
  getAuthorizationHeader,
  getDatalist,
  getIsIslamicBanking,
  getIsMultiPartnerJourney,
  getRoEmail,
  getSignatories
} from "../selectors/appConfig";
import { cpfCustomerConsent, kfsAcknowledgement } from "../../api/apiClient";
import { AccountDetails, CONVENTIONAL, ISLAMIC } from "../../constants";
import { updateProspect } from "../actions/appConfig";
import { formattedAccTimeStamp } from "../../utils/getAcceptedTimeStamp/getAcceptedTimeStamp";
import { getSelectedSignatoryIndex } from "../selectors/stakeholders";

const extractNumber = value => value?.replace(/\D/g, "") ?? "";

export function* sendKfsMail({ payload: { docModificationInfo } }) {
  try {
    const headers = yield select(getAuthorizationHeader);
    headers.headers["client-id"] = "WBA-STP";
    const signatories = yield select(getSignatories);
    const selectedSignatoryIndex = yield select(getSelectedSignatoryIndex);
    const companyEmailAddress = yield select(getApplicantEmail);
    const accountType = yield select(getAccountType);
    const isIslamic = yield select(getIsIslamicBanking);
    const roEmail = yield select(getRoEmail);
    const dataList = yield select(getDatalist);
    const isMultiPartnerJourney = yield select(getIsMultiPartnerJourney);
    const customerName = startCase(
      signatories[selectedSignatoryIndex].editedFullName.toLowerCase()
    );
    const signatoryId = signatories[selectedSignatoryIndex].signatoryId;
    const selectedSignatoryFirstName = signatories[selectedSignatoryIndex].firstName;
    const individualId = `${extractNumber(signatoryId)}_${selectedSignatoryFirstName}`;
    const dataListBccIds = [dataList.kfsBCCEmail[0]?.displayText];
    let toIds = [companyEmailAddress];
    let customerEmailAddress = "";

    if (isMultiPartnerJourney) {
      customerEmailAddress =
        signatories[selectedSignatoryIndex]?.contactDetails?.editedPrimaryEmailId;
      toIds = [customerEmailAddress];
    }

    const isElite = accountType === "RAKelite";
    const entitySubType = isIslamic ? "ISL" : "CON";
    const bccIds = roEmail ? [...dataListBccIds, roEmail] : dataListBccIds;
    const accountCategory = isIslamic ? ISLAMIC : CONVENTIONAL;
    const subProductCode = AccountDetails[accountType].subProductCode[accountCategory];
    const body = {
      customerName,
      customerEmailAddress,
      subProductCode,
      isElite,
      customerSegment: "SME",
      entitySubType,
      productClassificationSubType: "ACT",
      toIds,
      bccIds,
      docModificationInfo,
      individualId
    };
    yield put(
      termsAndConditionsLoading({
        isLoadingPdf: true
      })
    );
    yield call(kfsAcknowledgement.sendMail, body, headers);
    yield put(
      termsAndConditionsAccepted({
        kfs: true
      })
    );
    yield put(
      updateProspect({
        [`prospect.signatoryInfo[${selectedSignatoryIndex}].consentInfo`]: {
          ...signatories[selectedSignatoryIndex]?.consentInfo,
          kfsConsent: { accept: true, timestamp: formattedAccTimeStamp(new Date()) }
        }
      })
    );
    yield put(
      termsAndConditionsLoading({
        isLoadingPdf: false
      })
    );
  } catch (error) {
    log(error);
    yield put(
      termsAndConditionsAccepted({
        kfs: false
      })
    );
    yield put(
      termsAndConditionsLoading({
        isLoadingPdf: false
      })
    );
  }
}

export function* sendCustomerConsentToCPF({ payload: { docModificationInfo, consentType } }) {
  try {
    const headers = yield select(getAuthorizationHeader);
    const signatoryIndex = yield select(getSelectedSignatoryIndex);
    headers.headers["client-id"] = "WBA-STP";
    const signatories = yield select(getSignatories);
    const signatoryId = signatories[signatoryIndex].signatoryId;
    const selectedSignatoryFirstName = signatories[signatoryIndex].firstName;
    const individualId = `${extractNumber(signatoryId)}_${selectedSignatoryFirstName}`;

    const isIslamic = yield select(getIsIslamicBanking);
    const body = {
      processName: consentType,
      type: isIslamic ? ISLAMIC : CONVENTIONAL,
      individualId,
      consentRequests: [
        {
          key: consentType,
          value: "YES"
        }
      ],
      docModificationInfo
    };
    yield put(
      termsAndConditionsLoading({
        isLoadingPdf: true
      })
    );
    yield call(cpfCustomerConsent.send, body, headers);
    const acceptedTimeStamp = formattedAccTimeStamp(new Date());
    if (consentType === "TNC_CONSENT") {
      yield put(
        updateProspect({
          [`prospect.signatoryInfo[${signatoryIndex}].consentInfo`]: {
            ...signatories[signatoryIndex].consentInfo,
            otherTncConsent: { accept: true, timestamp: acceptedTimeStamp }
          }
        })
      );
      yield put(
        termsAndConditionsAccepted({
          generalTCs: true
        })
      );
    } else {
      yield put(
        updateProspect({
          [`prospect.signatoryInfo[${signatoryIndex}].consentInfo`]: {
            ...signatories[signatoryIndex]?.consentInfo,
            aecbConsent: { accept: true, timestamp: acceptedTimeStamp },
            ftsConsent: { accept: true, timestamp: acceptedTimeStamp },
            norblocConsent: { accept: true, timestamp: acceptedTimeStamp }
          }
        })
      );
      yield put(
        termsAndConditionsAccepted({
          authorisation: true
        })
      );
    }
    yield put(
      termsAndConditionsLoading({
        isLoadingPdf: false
      })
    );
  } catch (error) {
    log(error);
    if (consentType === "TNC_CONSENT") {
      yield put(
        termsAndConditionsAccepted({
          generalTCs: false
        })
      );
    } else {
      yield put(
        termsAndConditionsAccepted({
          authorisation: false
        })
      );
    }
    yield put(
      termsAndConditionsLoading({
        isLoadingPdf: false
      })
    );
  }
}

export default function* termsAndConditionsSaga() {
  yield all([takeLatest(SEND_KFS_MAIL, sendKfsMail)]);
  yield all([takeLatest(SEND_CUSTOMER_CONSENT_TO_CPF, sendCustomerConsentToCPF)]);
}
